/**
 * Work items tab — filter defaults, labels, and display helpers (extend as API grows).
 */
import {
  WORK_ITEMS_REPORTER_IDS_QUERY_PARAM,
  WORK_ITEMS_PARTICIPANT_USER_IDS_QUERY_PARAM,
  applyShowEpicsFilterParam,
  applyShowOnHoldFilterParam,
} from "../../../../Redux/Thunks/projectManagementThunkUtils";
import { partitionWorkItemListStatusFilter } from "../../../Issues/issueMappers";
import { normalizeWorkItemStatusCategoriesForApi } from "@/constants/workItemStatusCategoryTheme";

export { partitionWorkItemListStatusFilter };
import { DEFAULT_LIST_PAGE_SIZE } from "@/Utility/Pagination/listPaginationConstants";

export { WORK_ITEMS_REPORTER_IDS_QUERY_PARAM };
export { WORK_ITEMS_PARTICIPANT_USER_IDS_QUERY_PARAM };

export const ISSUES_LISTING_FILTER_DEFAULT = {
  search: "",
  type: [],
  status: [],
  priority: [],
  assignee: [],
  reporter: [],
  team: [],
  /** When false, epic work items are hidden (list + export). Default false = hide epics. */
  showEpics: false,
  /** All / My work items lists only — excludes CLOSED/DONE-category work items when true. Default true = hide done. */
  hideDone: true,
};

/** Non-empty filter value for API bodies (arrays omit when empty). */
export function normalizeListingFilterIds(val) {
  if (val == null || val === "") return null;
  if (Array.isArray(val)) return val.length ? val : null;
  return val;
}

/**
 * Shared listing scope for global Kanban (saved filters, quick filter, etc.).
 * @param {{ includeShowEpics?: boolean, includeShowOnHold?: boolean }} [options]
 */
function buildGlobalKanbanScopeParams(
  listingFilter,
  { page = 1, limit = 100 } = {},
  { includeShowEpics = false, includeShowOnHold = false } = {}
) {
  const lf = listingFilter && typeof listingFilter === "object" ? listingFilter : {};
  const params = { page, limit };
  const searchQ = String(lf.search ?? "").trim();

  const proj = normalizeListingFilterIds(lf.project);
  if (proj) params.projectId = proj;
  const cli = normalizeListingFilterIds(lf.client);
  if (cli) params.clientId = cli;
  const tp = normalizeListingFilterIds(lf.type);
  if (tp) params.typeIds = tp;
  const { categories, ids } = partitionWorkItemListStatusFilter(lf.status);
  if (categories.length) {
    const norm = normalizeWorkItemStatusCategoriesForApi(categories);
    params.statusCategories = norm.length === 1 ? norm[0] : norm;
  }
  if (ids.length) params.statusIds = ids.length === 1 ? ids[0] : ids;
  const pr = normalizeListingFilterIds(lf.priority);
  if (pr) params.priorityIds = pr;

  const asg = normalizeListingFilterIds(lf.assignee);
  if (asg) params.assigneeId = asg;
  const reporterIdsAll = normalizeListingFilterIds(lf.reporter);
  if (reporterIdsAll) params[WORK_ITEMS_REPORTER_IDS_QUERY_PARAM] = reporterIdsAll;

  if (searchQ) params.search = searchQ;
  if (Array.isArray(lf.team) && lf.team.length > 0) params.teamIds = lf.team;
  if (includeShowEpics) applyShowEpicsFilterParam(params, lf.showEpics);
  if (includeShowOnHold) applyShowOnHoldFilterParam(params, lf.showOnHold);
  return params;
}

/**
 * Query params for GET `/work-items/kanban/initial` — listing filters + optional `showOnHold` (no `showEpics`).
 * Quick filter people scope uses `assignee` → API `assigneeId`.
 */
export function buildGlobalKanbanFetchParams(listingFilter, { page = 1, limit = 100 } = {}) {
  return buildGlobalKanbanScopeParams(
    listingFilter,
    { page, limit },
    {
      includeShowEpics: false,
      includeShowOnHold: true,
    }
  );
}

function toIdList(val) {
  if (val == null || val === "") return [];
  if (Array.isArray(val)) return val.map((x) => String(x)).filter(Boolean);
  return String(val)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Map API / legacy saved-search JSON into listing filter field names (assignee, type, …).
 * Call before merging into {@link ISSUES_LISTING_FILTER_DEFAULT}.
 */
export function normalizeSavedSearchListingFilters(raw) {
  if (!raw || typeof raw !== "object") return {};
  const o = { ...raw };
  if (o.assigneeId != null && (!Array.isArray(o.assignee) || o.assignee.length === 0)) {
    o.assignee = toIdList(o.assigneeId);
    delete o.assigneeId;
  }
  if (o.projectId != null && (!Array.isArray(o.project) || o.project.length === 0)) {
    o.project = toIdList(o.projectId);
    delete o.projectId;
  }
  if (o.clientId != null && (!Array.isArray(o.client) || o.client.length === 0)) {
    o.client = toIdList(o.clientId);
    delete o.clientId;
  }
  if (o.typeIds != null && (!Array.isArray(o.type) || o.type.length === 0)) {
    o.type = toIdList(o.typeIds);
    delete o.typeIds;
  }
  if (o.priorityIds != null && (!Array.isArray(o.priority) || o.priority.length === 0)) {
    o.priority = toIdList(o.priorityIds);
    delete o.priorityIds;
  }
  if (o.statusIds != null && (!Array.isArray(o.status) || o.status.length === 0)) {
    o.status = toIdList(o.statusIds);
    delete o.statusIds;
  }
  if (o.statusCategories != null && (!Array.isArray(o.status) || o.status.length === 0)) {
    const cats = Array.isArray(o.statusCategories)
      ? o.statusCategories
      : String(o.statusCategories)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
    o.status = cats.map((c) => String(c).trim().toUpperCase().replace(/\s+/g, "_"));
    delete o.statusCategories;
  }
  if (o.reporterIds != null && (!Array.isArray(o.reporter) || o.reporter.length === 0)) {
    o.reporter = toIdList(o.reporterIds);
    delete o.reporterIds;
  }
  /** Legacy saved searches / URLs used participantUserIds — map to assignee. */
  if (o.participantUserIds != null && (!Array.isArray(o.assignee) || o.assignee.length === 0)) {
    o.assignee = toIdList(o.participantUserIds);
  }
  delete o.participantUserIds;
  if (o.showEpics != null) {
    o.showEpics = o.showEpics === true || o.showEpics === "true";
  }
  return o;
}

/** Page size for work item list APIs: project tab, GET `/work-items`, GET `/work-items/my`. */
export const WORK_ITEMS_API_PAGE_SIZE = DEFAULT_LIST_PAGE_SIZE;
/** @deprecated Use WORK_ITEMS_API_PAGE_SIZE — kept for existing imports. */
export const PROJECT_WORK_ITEMS_PAGE_SIZE = WORK_ITEMS_API_PAGE_SIZE;

/** Filter-only fields for project work-items (list + export) — aligned with IssueList filters. */
export function buildProjectWorkItemsFilterBody(listingFilter) {
  const lf = listingFilter && typeof listingFilter === "object" ? listingFilter : {};
  const body = {};
  const typeIds = normalizeListingFilterIds(lf.type);
  if (typeIds) body.typeIds = typeIds;
  const { categories, ids } = partitionWorkItemListStatusFilter(lf.status);
  if (categories.length) {
    const norm = normalizeWorkItemStatusCategoriesForApi(categories);
    body.statusCategories = norm.length === 1 ? norm[0] : norm;
  }
  if (ids.length) body.statusIds = ids.length === 1 ? ids[0] : ids;
  const priorityIds = normalizeListingFilterIds(lf.priority);
  if (priorityIds) body.priorityIds = priorityIds;
  const assigneeId = normalizeListingFilterIds(lf.assignee);
  if (assigneeId) body.assigneeId = assigneeId;
  const reporterIdsVal = normalizeListingFilterIds(lf.reporter);
  if (reporterIdsVal) body[WORK_ITEMS_REPORTER_IDS_QUERY_PARAM] = reporterIdsVal;
  if (Array.isArray(lf.team) && lf.team.length > 0) body.teamIds = lf.team;
  const sq = String(lf.search ?? "").trim();
  if (sq) body.search = sq;
  applyShowEpicsFilterParam(body, lf.showEpics);
  return body;
}

/**
 * GET project work-items query body from Work Items tab filter state (aligned with IssueList applyFilters).
 * @param {object} listingFilter
 * @param {{ page?: number, limit?: number }} [pagination]
 */
export function buildProjectIssuesListBody(listingFilter, pagination = {}) {
  const page = pagination.page != null ? Number(pagination.page) : 1;
  const limit = pagination.limit != null ? Number(pagination.limit) : PROJECT_WORK_ITEMS_PAGE_SIZE;
  return {
    limit: Number.isFinite(limit) && limit > 0 ? limit : PROJECT_WORK_ITEMS_PAGE_SIZE,
    page,
    ...buildProjectWorkItemsFilterBody(listingFilter),
  };
}
